import { Box, Flex, Stack, Text } from '@mantine/core'
import { useTranslations } from 'next-intl'

import CloseMarketIcon from '@/app/[locale]/price/stocks/[symbol]/_components/Icon/CloseMarket'
import PostMarketIcon from '@/app/[locale]/price/stocks/[symbol]/_components/Icon/PostMarket'
import PreMarketIcon from '@/app/[locale]/price/stocks/[symbol]/_components/Icon/PreMarket'
import { useStockContext } from '@/app/[locale]/price/stocks/[symbol]/_contexts/StockContext'
import AnimatedPrice from '@/components/AnimatedPrice'
import { StockSessionType } from '@/types/stock'
import { fiatCurrencyFormat } from '@/utils/number'

import { formatChangePercent, getPercentChangeColor } from '../utils'

interface ChartInfoTitleProps {
  stokcPrice?: number
  stockPriceChange?: number
  isMouseInChart: boolean
  currency: string
}

function getSessionIndicatorConfig(sessionType: StockSessionType, t: (key: string) => string) {
  switch (sessionType) {
    case StockSessionType.EarlyTrading:
      return {
        label: t('preMarket'),
        icon: <PreMarketIcon />,
        showPrice: true,
      }
    case StockSessionType.LateTrading:
      return {
        label: t('afterHours'),
        icon: <PostMarketIcon />,
        showPrice: true,
      }
    case StockSessionType.NoTrading:
      return {
        label: t('marketClosed'),
        icon: <CloseMarketIcon />,
        showPrice: true,
      }
    default:
      return null
  }
}

export default function ChartInfoTitle({
  stokcPrice,
  stockPriceChange,
  isMouseInChart,
  currency,
}: ChartInfoTitleProps) {
  const { stock } = useStockContext()
  const t = useTranslations('chart')
  const changeColor = getPercentChangeColor(stockPriceChange ?? 0)
  const changeText = stockPriceChange ? formatChangePercent(stockPriceChange * 100) : void 0

  const sessionConfig =
    stock.session_type && stock.session_type !== StockSessionType.CoreTrading
      ? getSessionIndicatorConfig(stock.session_type, t)
      : null

  const livePriceFormatted =
    sessionConfig && stock.live_price !== undefined ? fiatCurrencyFormat(stock.live_price) : null
  const livePricePctFormatted =
    sessionConfig && stock.live_price_pct !== undefined
      ? formatChangePercent(stock.live_price_pct)
      : null
  const livePriceChangeColor =
    stock.live_price_pct !== undefined
      ? getPercentChangeColor(stock.live_price_pct / 100)
      : undefined

  return (
    <Box>
      <Stack gap={12}>
        <Flex h={40} justify={'flex-start'} align={'center'} gap={8}>
          <Flex align={'flex-end'} gap={2}>
            <AnimatedPrice
              // to prevent when mouse out of chart would have a price animate
              key={`${isMouseInChart}`}
              variant={'balances'}
              price={stokcPrice}
              tokenSymbol={stock.symbol}
              disabledPriceAnimate={isMouseInChart}
              // when mouse inchart, would use the data in line chart api
              // the origin data was converted, not need converted again
              disabledConvert={isMouseInChart}
              isDecimalGrey
            />
            <Text c={'var(--content-tertiary)'} variant={'body'}>
              {currency}
            </Text>
          </Flex>
          <Box h={20}>
            <Text c={changeColor} variant={'body'}>
              {changeText}
            </Text>
          </Box>
        </Flex>

        {sessionConfig && (
          <Flex h={20} justify={'flex-start'} align={'center'} gap={4}>
            {sessionConfig.icon}
            <Text c={'var(--content-secondary)'} fz={14} fw={500} lh={'20px'}>
              {sessionConfig.label}
            </Text>
            {sessionConfig.showPrice && livePriceFormatted && livePricePctFormatted && (
              <>
                <AnimatedPrice
                  price={stock.live_price}
                  tokenSymbol={stock.symbol}
                  disabledPriceAnimate={true}
                  disabledConvert={false}
                  isDecimalGrey
                  c="white"
                  fz={14}
                  fw={500}
                  lh={'20px'}
                />

                <Text c={'var(--content-tertiary)'} fz={14} fw={500} lh={'20px'}>
                  {currency}
                </Text>
                <Text c={livePriceChangeColor} fz={14} fw={500} lh={'20px'}>
                  {livePricePctFormatted}
                </Text>
              </>
            )}
          </Flex>
        )}
      </Stack>
    </Box>
  )
}
